import { z } from "zod";
import type { EmpRole } from "./access";

/**
 * Who can do what with broadcasts: a matrix of skyEmployee role × permission,
 * set by each organisation in Messaging & broadcasts → Who can do what.
 *
 * Unlike the rest of skyEmployee's permissions (ROLE_CAPABILITIES, fixed per
 * role), these are configurable — but only inside safe limits, fixed here:
 *
 *   - "all": everyone in the organisation. Only Admins can hold it.
 *   - "scope": a Manager's own locations and teams (and, for things they
 *     create, like templates and lists, their own). The server always clamps
 *     a Manager to their scope; no setting can widen it.
 *   - "off".
 *
 * Users never hold broadcast permissions: a broadcast reaches people, so
 * sending one needs a role with a scope. Some cells are locked so nobody can
 * lock their organisation out (an Admin always manages the settings), or
 * because a level can't mean anything (organisation-wide settings have no
 * "their scope").
 */

export const BROADCAST_PERMISSIONS = [
  "send",
  "allowReplies",
  "urgent",
  "readReplies",
  "skipApproval",
  "templates",
  "keywords",
  "automations",
  "lists",
  "fields",
  "reports",
  "contacts",
  "settings",
] as const;
export type BroadcastPermission = (typeof BROADCAST_PERMISSIONS)[number];

export const BROADCAST_LEVELS = ["all", "scope", "off"] as const;
export type BroadcastLevel = (typeof BROADCAST_LEVELS)[number];

export const BROADCAST_PERMISSION_LABELS: Record<
  BroadcastPermission,
  { label: string; hint: string }
> = {
  send: { label: "Send broadcasts", hint: "Write, schedule and send broadcasts." },
  allowReplies: {
    label: "Allow replies on a broadcast",
    hint: "Turn replies on for a broadcast they send.",
  },
  urgent: {
    label: "Send urgent broadcasts",
    hint: "Urgent broadcasts can get through sending hours and muting, as the settings say.",
  },
  readReplies: { label: "Read reply threads", hint: "See and answer replies from staff." },
  skipApproval: {
    label: "Send without approval",
    hint: "Their broadcasts never wait for approval.",
  },
  templates: { label: "Manage templates", hint: "Create, change and delete templates." },
  keywords: { label: "Manage keywords", hint: "Words staff send to get a set answer." },
  automations: {
    label: "Manage automations",
    hint: "Messages sent automatically, such as on a start date or birthday.",
  },
  lists: { label: "Manage lists", hint: "Saved groups of people to send to." },
  fields: {
    label: "Manage fields",
    hint: "The details about people that audiences and personal touches use.",
  },
  reports: { label: "See reports", hint: "Who a broadcast reached and who read it." },
  contacts: {
    label: "Manage contacts",
    hint: "Mobile numbers and opt-outs: who can be texted, and importing numbers.",
  },
  settings: { label: "Change broadcast settings", hint: "Everything in Messaging & broadcasts." },
};

/** What "scope" means for each permission, in plain words, for the Manager column. */
export const SCOPE_MEANING: Partial<Record<BroadcastPermission, string>> = {
  send: "their locations and teams",
  urgent: "their locations and teams",
  readReplies: "their locations and teams",
  reports: "their own broadcasts, their people only",
  templates: "their own templates",
  keywords: "their own keywords",
  automations: "their own, for their people",
  lists: "their own lists, their people only",
  contacts: "their people only",
  allowReplies: "on",
  skipApproval: "on",
};

/** The roles the matrix covers, in column order. */
export const BROADCAST_ROLES: readonly EmpRole[] = ["admin", "controller", "employee"];

/**
 * The levels each role may hold for each permission. One level means the cell
 * is locked. Admins: on (all) or off. Managers: their scope or off. Users: off.
 */
export function allowedLevels(role: EmpRole, permission: BroadcastPermission): BroadcastLevel[] {
  if (role === "employee") return ["off"];
  if (role === "admin") return permission === "settings" ? ["all"] : ["all", "off"];
  // Organisation-wide definitions have no "their scope".
  if (permission === "settings" || permission === "fields") return ["off"];
  return ["scope", "off"];
}

export type BroadcastMatrix = Record<EmpRole, Record<BroadcastPermission, BroadcastLevel>>;

/** The starting matrix (decision D-3). */
export const DEFAULT_BROADCAST_MATRIX: BroadcastMatrix = {
  admin: Object.fromEntries(BROADCAST_PERMISSIONS.map((p) => [p, "all"])) as Record<
    BroadcastPermission,
    BroadcastLevel
  >,
  controller: {
    send: "scope",
    allowReplies: "scope",
    urgent: "off",
    readReplies: "scope",
    skipApproval: "scope",
    templates: "off",
    keywords: "off",
    automations: "off",
    lists: "scope",
    fields: "off",
    reports: "scope",
    contacts: "scope",
    settings: "off",
  },
  employee: Object.fromEntries(BROADCAST_PERMISSIONS.map((p) => [p, "off"])) as Record<
    BroadcastPermission,
    BroadcastLevel
  >,
};

/**
 * A stored matrix made safe: every cell present, each at a level its role may
 * hold (anything else takes the default). So a matrix saved before a limit
 * was tightened can never grant more than the limits allow.
 */
export function normaliseMatrix(stored: unknown): BroadcastMatrix {
  const src = stored && typeof stored === "object" ? (stored as Record<string, unknown>) : {};
  const out = {} as BroadcastMatrix;
  for (const role of BROADCAST_ROLES) {
    const row =
      src[role] && typeof src[role] === "object" ? (src[role] as Record<string, unknown>) : {};
    out[role] = {} as Record<BroadcastPermission, BroadcastLevel>;
    for (const p of BROADCAST_PERMISSIONS) {
      const allowed = allowedLevels(role, p);
      const v = row[p];
      const fallback = DEFAULT_BROADCAST_MATRIX[role][p];
      out[role][p] =
        typeof v === "string" && (allowed as string[]).includes(v)
          ? (v as BroadcastLevel)
          : allowed.includes(fallback)
            ? fallback
            : allowed[0];
    }
  }
  return out;
}

/** The validator for a saved matrix: every cell, each at a level its role may hold. */
export const broadcastMatrixSchema = z
  .object(
    Object.fromEntries(
      BROADCAST_ROLES.map((role) => [
        role,
        z
          .object(
            Object.fromEntries(
              BROADCAST_PERMISSIONS.map((p) => [
                p,
                z.enum(BROADCAST_LEVELS).refine((v) => allowedLevels(role, p).includes(v), {
                  message: `That level isn't available for ${BROADCAST_PERMISSION_LABELS[p].label.toLowerCase()}.`,
                }),
              ]),
            ),
          )
          .strict(),
      ]),
    ),
  )
  .strict();

/** One person's broadcast permissions, as the server resolved them. */
export type BroadcastAccess = Record<BroadcastPermission, BroadcastLevel>;

export const NO_BROADCAST_ACCESS: BroadcastAccess = DEFAULT_BROADCAST_MATRIX.employee;

/** True when the person can do anything at all with broadcasts (the Manage → Broadcasts menu). */
export function hasAnyBroadcastAccess(access: BroadcastAccess | null | undefined): boolean {
  if (!access) return false;
  return (
    [
      "send",
      "templates",
      "lists",
      "fields",
      "reports",
      "readReplies",
      "keywords",
      "automations",
      "contacts",
    ] as const
  ).some((p) => access[p] !== "off");
}

export interface BroadcastMatrixState {
  matrix: BroadcastMatrix;
  /** Bumped on every save; 0 when it has never been saved. */
  version: number;
}
